import { faArrowRight, faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import React, { useEffect, useState, useTransition } from "react";
import { twMerge } from "tailwind-merge";

import { Card } from "src/base-components/Card";
import { Icon } from "src/base-components/Icon";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Callout } from "src/design-system/Callout";
import { MissingSchema } from "src/parentFlowNodes/MissingSchema";
import {
  SCHEMA_MAPPING_ARROW_WIDTH,
  SchemaProperty,
} from "src/parentFlowNodes/SchemaProperty";
import { SchemaError } from "src/parentFlowNodes/flowNode/types";
import { buildSchemaTabUrl } from "src/parentFlowNodes/utils";
import { SchemaOptions } from "src/router/SearchParams";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { SchemaUI } from "src/schema/schemaMappingUtils";

type Props = {
  title: string;
  description?: React.ReactNode;
  childFlowName: string;
  schema?: SchemaUI;
  isOutput?: boolean;
  childFlowId: string;
  childFlowSlug: string;
  childVersionId: string;
  isNodeStale: boolean;
  readonly?: boolean;
  hasError?: boolean;
  fieldMappingErrors: Record<string, SchemaError>;
  isLoopNode?: boolean;
  highlightedFields: string[];
  lazyInputs: boolean;
};

const SkeletonRow = () => (
  <div className="flex items-center gap-x-3">
    <div className="w-1/2 min-w-0 rounded-lg">
      <SkeletonPlaceholder height="h-8" />
    </div>
    <div className={SCHEMA_MAPPING_ARROW_WIDTH}>
      <Icon color="text-gray-500" icon={faArrowRight} size="xs" />
    </div>
    <div className="w-1/2 min-w-0 rounded-lg">
      <SkeletonPlaceholder height="h-8" />
    </div>
  </div>
);

export const DataMap: React.FC<Props> = ({
  lazyInputs,
  schema,
  title,
  description,
  childFlowName,
  childFlowId,
  childVersionId,
  isOutput = false,
  readonly = false,
  hasError = false,
  fieldMappingErrors,
  isNodeStale,
  isLoopNode = false,
  highlightedFields,
}) => {
  const { orgId, workspace } = useAuthoringContext();
  const [displayedCount, setDisplayedCount] = useState(0);
  const [, startTransition] = useTransition();

  useEffect(() => {
    // checking if we have already displayed all the properties
    if (
      !schema?.properties.length ||
      displayedCount >= schema.properties.length
    ) {
      return;
    }

    startTransition(() => {
      // adding 20 properties at a time, which are rendered in background
      // avoids frozen UI when there are too many properties to render
      setDisplayedCount((prev) => prev + 20);
    });
  }, [startTransition, displayedCount, schema?.properties.length]);

  return (
    <div className="mb-4 flex flex-col gap-y-3">
      {hasError && isNodeStale && (
        <Callout icon={faInfoCircle} type="warning">
          Map required fields to complete configuration
        </Callout>
      )}
      <span className="text-gray-800 font-inter-semibold-13px">{title}</span>
      {description && (
        <p className="text-gray-500 font-inter-normal-12px">{description}</p>
      )}
      <Card variant={schema ? "default" : "inverted"}>
        <Card.Content>
          <div className="flex flex-col gap-y-2">
            {!schema ? (
              <MissingSchema
                childFlowId={childFlowId}
                childVersionId={childVersionId}
                flowName={childFlowName}
                isOutput={isOutput}
                warn={isNodeStale}
              />
            ) : (
              <>
                <div
                  className={twMerge(
                    "mb-1 flex w-full gap-x-3",
                    isOutput && "flex-row-reverse",
                  )}
                >
                  <p className="w-1/2 text-gray-500 font-inter-normal-12px">
                    Current Decision Flow
                  </p>
                  <div className={SCHEMA_MAPPING_ARROW_WIDTH} />
                  <p className="w-1/2 text-gray-500 font-inter-normal-12px">
                    {childFlowName}
                  </p>
                </div>
                {schema.properties.map((field, index) => {
                  if (index > displayedCount) {
                    return <SkeletonRow key={field.fieldName} />;
                  }

                  return (
                    <SchemaProperty
                      key={field.fieldName}
                      field={field}
                      fieldError={!!fieldMappingErrors[field.fieldName]}
                      highlighted={highlightedFields.includes(field.fieldName)}
                      isLoopNode={isLoopNode}
                      isOutput={isOutput}
                      lazy={lazyInputs}
                      readonly={readonly}
                      schemaTabUrl={buildSchemaTabUrl(
                        orgId,
                        workspace.id,
                        childFlowId,
                        childVersionId,
                        isOutput ? SchemaOptions.Output : SchemaOptions.Input,
                      )}
                      shouldDisplayError={isNodeStale}
                    />
                  );
                })}
              </>
            )}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};
